import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useContext,
} from "react";
import debounce from "lodash.debounce";
import { StyledInput, FormControl } from "../../components/Form/FormControls";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { MdOutlineRocketLaunch, MdEditDocument } from "react-icons/md";
import Modal from "../../components/Modal";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Grid,
  Alert,
  Button,
  ButtonGroup,
  CircularProgress,
  Backdrop,
  Box,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Collapse,
  Typography,
} from "@mui/material";
import { AuthContext } from "../../contexts/AuthContext";
import { availableDataSourcesForBP } from "../../api/dataSourceQueries";
import { useApi } from "../../api/useApi";
import SourceBuilder from "./bpmanager/SourceBuilder";
import ApplyBlueprintResultReport from "./bpmanager/ApplyBlueprintResultReport";
import SourcePairingGrid from "./bpmanager/SourcePairingGrid";
import { useSourcePairings } from "./bpmanager/useSourcePairings";
import {
  useApplyBlueprint,
  BlueprintApplyScope,
} from "../../api/useApplyBlueprint";
import ViewStandard from "../../views/RuleStandardPage/ViewStandard";
import { Scrollbars } from "react-custom-scrollbars";
import Permissions from "../../components/Form/PermissionsWithoutFormik";
import { formatTime } from "../../common/formats";

const AccessPoint = () => {
  const [myPatToken, setMyPatToken] = useState("");
  const [inputValue, setInputValue] = useState("");

  const debouncedSave = useMemo(
    () => debounce((newValue) => setMyPatToken(newValue), 1000),
    []
  );

  const updateValue = (newValue) => {
    setInputValue(newValue);
    debouncedSave(newValue);
  };

  return (
    <>
      <h3>Enter Your Personal Access Token to Access Policy Packs</h3>
      <p>
        <i>Keep this token secure, do not share this token with anyone!</i>
      </p>
      <FormControl>
        <StyledInput
          type="password"
          name="pta"
          label="Access Token"
          value={inputValue}
          placeholder={`Enter Your Personal Access Token`}
          onChange={(e) => updateValue(e?.target?.value)}
        />
      </FormControl>

      {myPatToken ? <ImportForm myPatToken={myPatToken} /> : null}
    </>
  );
};

// Shown once pairing resolution has settled with nothing to pair -- an empty/malformed blueprint, or
// an environment with no enabled sources to pair against. Without it the tab would look like it was
// still loading with no explanation.
const NoSourcePairings = () => (
  <Alert severity="warning" sx={{ my: 2 }}>
    No sources to pair. This blueprint has no source profiles, or no enabled
    data sources are available in this environment to pair them against.
  </Alert>
);

const DeployAll = ({ blueprint, setMode }) => {
  const [permissions, setPermissions] = useState([]);
  const [applyColumnConfigurations, setApplyColumnConfigurations] =
    useState(true);
  const [renameOnConflict, setRenameOnConflict] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const { apply, result, errors, loading } = useApplyBlueprint();
  const { pairings, sourceOptions, ready, setPairingTarget, toSourcePairings } =
    useSourcePairings(blueprint);

  // Deploy the entire blueprint in one pass: standards (created), instances, and (optionally) column
  // configurations. The backend reconciles/remaps everything by name using the pairings (scope = ALL).
  const deployAll = () => {
    apply({
      blueprint,
      scope: BlueprintApplyScope.All,
      sourcePairings: toSourcePairings(),
      permissions,
      applyColumnConfigurations,
      renameOnConflict,
      dryRun,
    });
  };

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={!ready}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {pairings?.length ? (
        <SourcePairingGrid
          pairings={pairings}
          sourceOptions={sourceOptions}
          onChange={setPairingTarget}
        />
      ) : (
        ready && <NoSourcePairings />
      )}

      <div style={{ marginTop: "1rem" }}>
        <h4>Select Permissions</h4>
        <Permissions callFn={setPermissions} permissions={permissions} />
      </div>

      <Box display="flex" justifyContent="center" gap={3} mt={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={applyColumnConfigurations}
              onChange={(e) => setApplyColumnConfigurations(e.target.checked)}
            />
          }
          label="Apply column configurations"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={renameOnConflict}
              onChange={(e) => setRenameOnConflict(e.target.checked)}
            />
          }
          label="Rename on conflict"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
          }
          label="Dry run (preview only)"
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Button
          onClick={deployAll}
          variant="contained"
          size="large"
          disabled={loading || !permissions?.length}
        >
          {loading
            ? "Deploying..."
            : dryRun
            ? "Preview Full Deployment"
            : "Deploy Entire Blueprint"}
        </Button>
      </Box>

      <ApplyBlueprintResultReport
        result={result}
        loading={loading}
        errors={errors}
      />
    </>
  );
};

///

const DeployPolicies = ({ blueprint, setMode }) => {
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [view, setView] = useState();
  const [permissions, setPermissions] = useState([]);
  const [renameOnConflict, setRenameOnConflict] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const { apply, result, errors, loading } = useApplyBlueprint();

  const uniquePolicies = blueprint?.sourceProfiles
    ?.flatMap((profile) => profile?.policyAndMappings)
    ?.reduce((acc, policy) => {
      if (!acc.some((p) => p.ruleStandardId === policy.ruleStandardId)) {
        acc.push(policy);
      }
      return acc;
    }, []);

  // Handle checkbox toggle for individual rows
  const handleToggle = (ruleStandardId) => {
    setSelectedPolicies((prev) =>
      prev.includes(ruleStandardId)
        ? prev.filter((id) => id !== ruleStandardId)
        : [...prev, ruleStandardId]
    );
  };

  // Handle Select All toggle
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = uniquePolicies.map((policy) => policy.ruleStandardId);
      setSelectedPolicies(allIds);
    } else {
      setSelectedPolicies([]);
    }
  };

  // Deploy only the selected standards: trim the blueprint's policyAndMappings to the checked
  // rule-standard ids and let the backend reconcile/create them (scope = STANDARDS). The backend
  // matches by fingerprint, so already-present standards are reported AlreadyExists, not duplicated.
  const deployPolicies = () => {
    const selected = new Set(selectedPolicies);
    const scopedBlueprint = {
      sourceProfiles: (blueprint?.sourceProfiles ?? []).map((sp) => ({
        ...sp,
        policyAndMappings: (sp?.policyAndMappings ?? []).filter((pam) =>
          selected.has(pam.ruleStandardId)
        ),
      })),
    };

    apply({
      blueprint: scopedBlueprint,
      scope: BlueprintApplyScope.Standards,
      permissions,
      renameOnConflict,
      dryRun,
    });
  };

  const allSelected =
    uniquePolicies.length > 0 &&
    selectedPolicies.length === uniquePolicies.length;
  const isIndeterminate =
    selectedPolicies.length > 0 &&
    selectedPolicies.length < uniquePolicies.length;

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#f5f5f5", // Light background to make it stand out
          padding: "16px",
          borderRadius: "8px", // Rounded corners
          border: "1px solid #ddd", // Subtle border
        }}
        mb={2}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: "bold", // Bold font for emphasis
            color: "#333", // Darker text color for readability
            fontSize: "1rem", // Adjust font size as needed
          }}
        >
          Ensure you have deployed these policies to the environment for which
          you wish to map within, if you attempt to map before, im not sure what
          happens, but good luck. If it already exists, and you need to update
          to latest, try removing the existing policy. If that doesn't work -
          It's Broken.
          <Typography component="span" color="error" fontWeight="bold">
            The Warning! ☢️
          </Typography>
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Policy Name</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uniquePolicies?.map((policy) => (
              <TableRow key={policy.ruleStandardId}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedPolicies.includes(policy.ruleStandardId)}
                    onChange={() => handleToggle(policy.ruleStandardId)}
                  />
                </TableCell>
                <TableCell>{policy.ruleStandardName}</TableCell>

                <TableCell>
                  {/* {loadingStatus[policy.ruleStandardId]?.status ===
                    "failed" && (
                    <Button
                      onClick={() => redeployPolicy(policy.ruleStandardId)}
                    >
                      Retry
                    </Button>
                  )} */}
                  <Button onClick={() => setView(policy)}>View Policy</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <div style={{ marginTop: "1rem" }}>
        <h4>Select Permissions</h4>
        <Permissions callFn={setPermissions} permissions={permissions} />
      </div>

      <Box display="flex" justifyContent="center" gap={3} mt={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={renameOnConflict}
              onChange={(e) => setRenameOnConflict(e.target.checked)}
            />
          }
          label="Rename on conflict"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
          }
          label="Dry run (preview only)"
        />
      </Box>

      {/* Centered Deploy Button */}
      <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Button
          onClick={deployPolicies}
          variant="contained"
          size="large"
          disabled={loading || !selectedPolicies?.length || !permissions?.length}
        >
          {loading
            ? "Deploying..."
            : dryRun
            ? "Preview Deployment"
            : "Deploy Selected Policies"}
        </Button>
      </Box>

      <ApplyBlueprintResultReport
        result={result}
        loading={loading}
        errors={errors}
      />

      {view && (
        <Modal title={view?.ruleStandardName} hide={() => setView()}>
          <Scrollbars autoHeightMax={window.innerHeight * 0.7} autoHeight>
            <ViewStandard standard={view?.policyStandardVersionTemplate} />
          </Scrollbars>
        </Modal>
      )}
    </>
  );
};

//

const DeployMappings = ({ blueprint, setMode }) => {
  const [view, setView] = useState();
  const [selectedPolicyMappings, setSelectedPolicyMappings] = useState([]);
  const [dryRun, setDryRun] = useState(false);
  const { apply, result, errors, loading } = useApplyBlueprint();
  const { pairings, sourceOptions, ready, setPairingTarget, toSourcePairings } =
    useSourcePairings(blueprint);

  // All rule-instance mappings across the blueprint, tagged with the source profile they came from.
  const mappings = (blueprint?.sourceProfiles ?? []).flatMap((profile) =>
    (profile?.policyAndMappings ?? []).map((pam) => ({
      ...pam,
      ogSourceName: pam.ogSourceName ?? profile.ogName,
    }))
  );

  const uniqueStandards = mappings.reduce((acc, policy) => {
    if (!acc.some((p) => p.ruleStandardId === policy.ruleStandardId)) {
      acc.push(policy);
    }
    return acc;
  }, []);

  const handleToggle = (versionId) => {
    setSelectedPolicyMappings((prev) =>
      prev.includes(versionId)
        ? prev.filter((id) => id !== versionId)
        : [...prev, versionId]
    );
  };

  const handleSelectAll = (event) => {
    setSelectedPolicyMappings(
      event.target.checked ? mappings.map((m) => m.ruleInstanceVersionId) : []
    );
  };

  // Deploy the selected instances. Standards are resolved (not created) against the target env; the
  // backend remaps columns/fragments by name using the source pairings below (scope = INSTANCES).
  const deployMappings = () => {
    const selected = new Set(selectedPolicyMappings);
    const scopedBlueprint = {
      sourceProfiles: (blueprint?.sourceProfiles ?? []).map((sp) => ({
        ...sp,
        policyAndMappings: (sp?.policyAndMappings ?? []).filter((pam) =>
          selected.has(pam.ruleInstanceVersionId)
        ),
      })),
    };

    apply({
      blueprint: scopedBlueprint,
      scope: BlueprintApplyScope.Instances,
      sourcePairings: toSourcePairings(),
      dryRun,
    });
  };

  const allSelected =
    mappings.length > 0 && selectedPolicyMappings.length === mappings.length;
  const isIndeterminate =
    selectedPolicyMappings.length > 0 &&
    selectedPolicyMappings.length < mappings.length;

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={!ready}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {pairings?.length ? (
        <SourcePairingGrid
          pairings={pairings}
          sourceOptions={sourceOptions}
          onChange={setPairingTarget}
        />
      ) : (
        ready && <NoSourcePairings />
      )}

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Mapping Instance Name</TableCell>
              <TableCell>Blueprint Source</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((mapping) => (
              <TableRow key={mapping.ruleInstanceVersionId}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedPolicyMappings.includes(
                      mapping.ruleInstanceVersionId
                    )}
                    onChange={() => handleToggle(mapping.ruleInstanceVersionId)}
                  />
                </TableCell>
                <TableCell>{mapping.ruleInstanceName}</TableCell>
                <TableCell>{mapping.ogSourceName}</TableCell>
                <TableCell>
                  <Button
                    onClick={() =>
                      setView(
                        uniqueStandards.find(
                          (p) => p?.ruleStandardId === mapping?.ruleStandardId
                        )
                      )
                    }
                  >
                    View Policy
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" mt={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
          }
          label="Dry run (preview only)"
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Button
          onClick={deployMappings}
          variant="contained"
          size="large"
          disabled={loading || !selectedPolicyMappings?.length}
        >
          {loading
            ? "Deploying..."
            : dryRun
            ? "Preview Deployment"
            : "Deploy Selected Mappings"}
        </Button>
      </Box>

      <ApplyBlueprintResultReport
        result={result}
        loading={loading}
        errors={errors}
      />

      {view && (
        <Modal title={view?.ruleStandardName} hide={() => setView()}>
          <Scrollbars autoHeightMax={window.innerHeight * 0.7} autoHeight>
            <ViewStandard standard={view?.policyStandardVersionTemplate} />
          </Scrollbars>
        </Modal>
      )}
    </>
  );
};

const ApplyColumnConfigurations = ({ blueprint, setMode }) => {
  const [selectedPlatformSource, setSelectedPlatformSource] = useState(null);
  const [selectedBlueprintSource, setSelectedBlueprintSource] = useState(null);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [dryRun, setDryRun] = useState(false);
  const { apply, result, errors, loading } = useApplyBlueprint();

  const [{ data: availableSources }] = useApi(availableDataSourcesForBP, {
    first: 9999,
    where: { enabled: { eq: true } },
    tagFilter: { exactMatch: false, tagIds: [] },
  });

  const sourcesAvail = availableSources?.availableDataSources?.edges ?? [];
  const blueprintSources = blueprint?.sourceProfiles ?? [];

  // Informational preview of name matches. The backend performs the authoritative match + apply
  // (data types AND flags); this just gives the user a sense of coverage before applying.
  const matchStats = useMemo(() => {
    if (!selectedPlatformSource || !selectedBlueprintSource) {
      return { matched: 0, total: 0, unmatched: 0 };
    }

    const platformColumns = selectedPlatformSource.columns || [];
    const blueprintColumns = selectedBlueprintSource.ogColumns || [];

    let matched = 0;
    platformColumns.forEach((platformCol) => {
      const bpCol = blueprintColumns.find((c) => c.name === platformCol.name);
      if (bpCol && bpCol.dataType) {
        matched++;
      }
    });

    return {
      matched,
      total: platformColumns.length,
      unmatched: platformColumns.length - matched,
    };
  }, [selectedPlatformSource, selectedBlueprintSource]);

  useEffect(() => {
    if (sourcesAvail?.length) {
      setSourceOptions(
        sourcesAvail.map((s) => ({
          label: s.node?.name,
          value: s.node?.id,
          columns: s.node?.columns,
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSources]);

  // Apply the blueprint source's column data types + flags onto the selected platform source,
  // matched by name. The backend does the matching and application (scope = COLUMNS).
  const applyConfigurations = () => {
    if (!selectedPlatformSource || !selectedBlueprintSource) {
      return;
    }

    apply({
      blueprint: { sourceProfiles: [selectedBlueprintSource] },
      scope: BlueprintApplyScope.Columns,
      sourcePairings: [
        {
          ogName: selectedBlueprintSource.ogName,
          targetName: selectedPlatformSource.label,
        },
      ],
      applyColumnConfigurations: true,
      dryRun,
    });
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          padding: "16px",
          borderRadius: "8px",
          border: "1px solid #ddd",
        }}
        mb={2}
      >
        <Typography variant="body1" sx={{ fontWeight: "bold", color: "#333" }}>
          Select a platform source and a blueprint source to apply column
          configurations. Platform columns are matched by name with blueprint
          columns, and the blueprint's data types and flags (primary key, unique,
          nullable, sensitive) are applied to the matching platform columns.
        </Typography>
      </Box>

      <Grid container spacing={3} mt={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ padding: 2 }}>
            <Typography variant="h6" mb={2}>
              Platform Source
            </Typography>
            <Autocomplete
              options={sourceOptions}
              getOptionLabel={(option) => option?.label || ""}
              value={selectedPlatformSource}
              onChange={(event, newValue) => setSelectedPlatformSource(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Platform Source"
                  placeholder="Type to search..."
                />
              )}
            />
            {selectedPlatformSource && (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  {selectedPlatformSource.columns?.length || 0} columns available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ padding: 2 }}>
            <Typography variant="h6" mb={2}>
              Blueprint Source
            </Typography>
            <Autocomplete
              options={blueprintSources}
              getOptionLabel={(option) => option?.ogName || ""}
              value={selectedBlueprintSource}
              onChange={(event, newValue) =>
                setSelectedBlueprintSource(newValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Blueprint Source"
                  placeholder="Type to search..."
                />
              )}
            />
            {selectedBlueprintSource && (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  {selectedBlueprintSource.ogColumns?.length || 0} column
                  configurations
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {selectedPlatformSource && selectedBlueprintSource && (
        <Box mt={3}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold">
              Match Analysis: {matchStats.matched} of {matchStats.total} platform
              columns match a blueprint column by name
            </Typography>
            <Typography variant="body2">
              {matchStats.matched} columns matched by name • {matchStats.unmatched}{" "}
              columns have no matching blueprint column (or no data type). Run a
              dry run below to see exactly what will change.
            </Typography>
          </Alert>
          <Paper sx={{ padding: 2 }}>
            <Typography variant="h6" mb={2}>
              Column Configuration Preview
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Column Name</TableCell>
                    <TableCell>Data Type</TableCell>
                    <TableCell>Additional Info</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedBlueprintSource.ogColumns
                    ?.slice(0, 10)
                    .map((col, index) => (
                      <TableRow key={index}>
                        <TableCell>{col.name}</TableCell>
                        <TableCell>{col.dataType || "N/A"}</TableCell>
                        <TableCell>{col.fullyQualifiedName || "N/A"}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            {selectedBlueprintSource.ogColumns?.length > 10 && (
              <Typography variant="caption" color="text.secondary" mt={1}>
                Showing 10 of {selectedBlueprintSource.ogColumns.length} columns
              </Typography>
            )}
          </Paper>
        </Box>
      )}

      <Box display="flex" justifyContent="center" mt={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
          }
          label="Dry run (preview only)"
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Button
          onClick={applyConfigurations}
          variant="contained"
          size="large"
          disabled={loading || !selectedPlatformSource || !selectedBlueprintSource}
        >
          {loading
            ? "Applying..."
            : dryRun
            ? "Preview Configuration"
            : "Apply Column Configurations"}
        </Button>
      </Box>

      <ApplyBlueprintResultReport
        result={result}
        loading={loading}
        errors={errors}
      />
    </>
  );
};

const BluePrintDeployment = ({
  blueprint,
  onSubmit,
  setSelectedBlueprint,
  setIsDeploying,
}) => {
  const [mode, setMode] = useState(2);
  return (
    <>
      <button
        onClick={() => {
          setSelectedBlueprint(null);
          setIsDeploying(false);
        }}
      >
        Back
      </button>
      <div>
        <h3>{blueprint?.name}</h3>
        <div>
          <p>Description: {blueprint?.description}</p>
        </div>
        <div>
          Edited Date: {formatTime(new Date(blueprint?.editedDate)) ?? "N/A"}
        </div>
      </div>

      <Box display="flex" justifyContent="center" sx={{ marginBottom: 2 }}>
        <ButtonGroup variant="outlined">
          {/* Use outlined variant for tab-like appearance */}
          <Button
            onClick={() => setMode(1)}
            variant={mode === 1 ? "contained" : "outlined"} // Highlight the active tab
          >
            Deploy All
          </Button>
          <Button
            onClick={() => setMode(2)}
            variant={mode === 2 ? "contained" : "outlined"} // Highlight the active tab
          >
            Deploy Policy Templates
          </Button>
          <Button
            onClick={() => setMode(3)}
            variant={mode === 3 ? "contained" : "outlined"} // Highlight the active tab
          >
            Deploy Mappings
          </Button>
          <Button
            onClick={() => setMode(4)}
            variant={mode === 4 ? "contained" : "outlined"} // Highlight the active tab
          >
            Apply Column Configurations
          </Button>
        </ButtonGroup>
      </Box>
      {mode === 1 && <DeployAll blueprint={blueprint} setMode={setMode} />}
      {mode === 2 && <DeployPolicies blueprint={blueprint} setMode={setMode} />}
      {mode === 3 && <DeployMappings blueprint={blueprint} setMode={setMode} />}
      {mode === 4 && (
        <ApplyColumnConfigurations blueprint={blueprint} setMode={setMode} />
      )}
    </>
  );
};

const BluePrintBuilder = ({
  blueprint,
  onSubmit,
  setSelectedBlueprint,
  setIsDeploying,
}) => {
  const { control, register, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      sourceProfiles: blueprint?.sourceProfiles ?? [],
    },
  });
  const { user } = useContext(AuthContext);
  const sourceProfiles = watch("sourceProfiles");

  const isEditing = blueprint?.name; // Check if the blueprint is not empty

  useEffect(() => {
    if (isEditing) {
      Object.entries(blueprint)?.forEach(([key, value]) => {
        if (key === "sourceProfiles") {
          setValue("sourceProfiles", value); // Ensure value structure is correct here
        } else {
          setValue(key, value); // Set other fields if needed
        }
      });
    }
  }, [blueprint, setValue, isEditing]);

  const handleFormSubmit = (data) => {
    const updatedData = {
      ...data,
      editedDate: new Date().toISOString(),
      editedBy: user?.emailAddress,
    };

    onSubmit(updatedData); // Pass the data to handleBlueprintSubmit
  };

  const processSourceProfiles = (selectedSources) => {
    setValue("sourceProfiles", selectedSources);
  };

  return (
    <>
      <h3>{isEditing ? "Edit" : "Create"} Blueprint</h3>{" "}
      <button
        onClick={() => {
          setSelectedBlueprint(null);
          setIsDeploying(false);
        }}
      >
        Back
      </button>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {isEditing ? (
          <Box mb={2} mt={2}>
            <h3>{blueprint?.name}</h3>
          </Box>
        ) : (
          <Box mb={2}>
            <label>Name</label>
            <input {...register("name")} type="text" />
          </Box>
        )}
        <Box mb={2}>
          <label>Description</label>
          <input {...register("description")} type="text" />
        </Box>

        <SourceBuilder
          sourceProfiles={sourceProfiles}
          processSourceProfiles={processSourceProfiles}
        />

        {/* Centered Deploy Button */}
        <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
          <Button type="submit" variant="contained" size="large">
            Save Blueprint
          </Button>
        </Box>
      </form>
    </>
  );
};

const BlueprintTable = ({
  myPatToken,
  onBlueprintLoad,
  onCreateNew,
  setIsDeploying,
}) => {
  const [blueprints, setBlueprints] = useState([]);

  // Remove BluePrint

  const handleRemoveBlueprintClick = (blueprint) => {
    if (
      window.confirm(
        `Are you sure you want to delete the blueprint "${blueprint.name}"?`
      )
    ) {
      removeBlueprintFromGit(blueprint.path);
    }
  };

  // Function to fetch the latest commit ID
  const fetchLatestCommitId = async () => {
    const refUrl = `https://dev.azure.com/basecapanalytics/Testing%20Tools/_apis/git/repositories/Testing%20Tools/refs?filter=heads/master&api-version=6.0`;

    const response = await fetch(refUrl, {
      method: "GET",
      headers: {
        Authorization: `Basic ${btoa(`:${myPatToken}`)}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch latest commit ID: ${response.statusText}`
      );
    }

    const data = await response.json();
    const latestCommitId = data.value[0]?.objectId;
    return latestCommitId;
  };

  const removeBlueprintFromGit = async (blueprintPath) => {
    try {
      const latestCommitId = await fetchLatestCommitId();

      const changes = [
        {
          changeType: "delete",
          item: { path: blueprintPath },
        },
      ];

      const requestBody = {
        refUpdates: [
          { name: "refs/heads/master", oldObjectId: latestCommitId },
        ],
        commits: [{ comment: `Deleted blueprint: ${blueprintPath}`, changes }],
      };

      const gitCommitURL = `https://dev.azure.com/basecapanalytics/Testing%20Tools/_apis/git/repositories/Testing%20Tools/pushes?api-version=6.0`;

      const response = await fetch(gitCommitURL, {
        method: "POST",
        headers: {
          Authorization: `Basic ${btoa(`:${myPatToken}`)}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete blueprint: ${response.statusText}`);
      }

      toast.success(`Blueprint ${blueprintPath} deleted successfully!`);

      fetchBlueprints(); // Refresh the list after deletion
    } catch (error) {
      console.error("Error deleting blueprint:", error);
      toast.error("Failed to delete the blueprint.");
    }
  };

  const fetchBlueprints = useCallback(() => {
    const listBlueprintsURL =
      "https://dev.azure.com/basecapanalytics/Testing%20Tools/_apis/git/repositories/Testing%20Tools/items?scopePath=%2Fblueprints&recursionLevel=full&versionDescriptor.version=master&api-version=6.0";

    fetch(listBlueprintsURL, {
      method: "GET",
      withCredentials: true,
      crossDomain: true,
      headers: {
        Authorization: "Basic " + btoa(":" + myPatToken),
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const blueprintFiles = data.value
          .filter((file) => file.path.endsWith(".json"))
          .map((file) => ({
            name: file.path.split("/").pop(),
            path: file.path,
          }));
        setBlueprints(blueprintFiles);
      })
      .catch((error) => {
        console.error("Error fetching blueprint files:", error);
      });
  }, [myPatToken]);

  useEffect(() => {
    fetchBlueprints();
  }, [fetchBlueprints]);

  const handleBlueprintClick = (blueprintPath) => {
    const blueprintURL = `https://dev.azure.com/basecapanalytics/Testing%20Tools/_apis/git/repositories/Testing%20Tools/items?path=${encodeURIComponent(
      blueprintPath
    )}&api-version=6.0`;

    fetch(blueprintURL, {
      method: "GET",
      headers: {
        Authorization: `Basic ${btoa(`:${myPatToken}`)}`,
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        onBlueprintLoad(data);
      })
      .catch((error) => {
        console.error("Error loading blueprint JSON:", error);
      });
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={onCreateNew}>
        Create New Blueprint
      </Button>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Blueprint Name</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blueprints.map((blueprint) => (
              <TableRow key={blueprint.name}>
                <TableCell>{blueprint.name}</TableCell>
                <TableCell>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                  >
                    <IconButton
                      title="Stage Deployment"
                      onClick={() => {
                        handleBlueprintClick(blueprint.path);
                        setIsDeploying(true);
                      }}
                      sx={{ fontSize: 32 }}
                    >
                      <MdOutlineRocketLaunch />
                    </IconButton>
                    <IconButton
                      title=""
                      onClick={() => handleBlueprintClick(blueprint.path)}
                      sx={{ fontSize: 32 }}
                    >
                      <MdEditDocument />
                    </IconButton>
                    <IconButton
                      title="Remove Blueprint"
                      onClick={() => handleRemoveBlueprintClick(blueprint)}
                      sx={{ fontSize: 32, color: "red" }}
                    >
                      🗑️
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const ImportForm = ({ myPatToken }) => {
  const [selectedBlueprint, setSelectedBlueprint] = useState(null);
  const [isDeploying, setIsDeploying] = useState();

  const handleBlueprintLoaded = (blueprintData) => {
    setSelectedBlueprint(blueprintData);
  };

  const handleCreateNewBlueprint = () => {
    setSelectedBlueprint({
      name: "",
    }); // Reset the form for a new blueprint
  };
  // Function to fetch the latest commit ID
  const fetchLatestCommitId = async () => {
    const refUrl = `https://dev.azure.com/basecapanalytics/Testing%20Tools/_apis/git/repositories/Testing%20Tools/refs?filter=heads/master&api-version=6.0`;

    const response = await fetch(refUrl, {
      method: "GET",
      headers: {
        Authorization: `Basic ${btoa(`:${myPatToken}`)}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch latest commit ID: ${response.statusText}`
      );
    }

    const data = await response.json();
    const latestCommitId = data.value[0]?.objectId;
    return latestCommitId;
  };

  // Function to check if a file exists
  const checkIfFileExists = async (filePath) => {
    const fileUrl = `https://dev.azure.com/basecapanalytics/Testing%20Tools/_apis/git/repositories/Testing%20Tools/items?path=${encodeURIComponent(
      filePath
    )}&api-version=6.0`;

    const response = await fetch(fileUrl, {
      method: "GET",
      headers: {
        Authorization: `Basic ${btoa(`:${myPatToken}`)}`,
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      return true;
    } else if (response.status === 404) {
      return false;
    } else {
      throw new Error(`Error checking if file exists: ${response.statusText}`);
    }
  };
  const handleBlueprintSubmit = async (data) => {
    try {
      const latestCommitId = await fetchLatestCommitId();

      // Determine if we're editing or adding a new blueprint
      const isEditing = selectedBlueprint?.name;

      // Set the file path based on whether we're editing or creating new
      const filePath = isEditing
        ? `/blueprints/${selectedBlueprint.name}.json`
        : data.name
        ? `/blueprints/${data.name}.json`
        : "/blueprints/unnamed.json";

      const fileContent = JSON.stringify(data, null, 2);

      let changes = [];

      if (isEditing) {
        // Perform an edit operation
        changes.push({
          changeType: "edit",
          item: { path: filePath },
          newContent: { content: fileContent, contentType: "rawtext" },
        });
      } else {
        // Adding a new blueprint

        const fileExists = await checkIfFileExists(filePath);
        if (fileExists) {
          throw new Error(`File ${filePath} already exists.`);
        }

        changes.push({
          changeType: "add",
          item: { path: filePath },
          newContent: { content: fileContent, contentType: "rawtext" },
        });
      }

      const commitMessage = isEditing
        ? `Updated blueprint: ${selectedBlueprint.name}`
        : `Created new blueprint: ${data.name}`;

      // Prepare the request body for the Git commit
      const requestBody = {
        refUpdates: [
          { name: "refs/heads/master", oldObjectId: latestCommitId },
        ],
        commits: [
          {
            comment: commitMessage,
            changes: changes,
          },
        ],
      };

      const gitCommitURL = `https://dev.azure.com/basecapanalytics/Testing%20Tools/_apis/git/repositories/Testing%20Tools/pushes?api-version=6.0`;

      // Send the request to commit the blueprint
      const response = await fetch(gitCommitURL, {
        method: "POST",
        headers: {
          Authorization: `Basic ${btoa(`:${myPatToken}`)}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`
        );
      }

      toast.success(
        `Blueprint "${isEditing ? selectedBlueprint.name : data.name}" ${
          isEditing ? "updated" : "created"
        } and committed!`
      );
      setSelectedBlueprint(null);
    } catch (error) {
      toast.error("Error saving blueprint.");
    }
  };

  return (
    <div>
      {selectedBlueprint !== null ? (
        isDeploying ? (
          <BluePrintDeployment
            blueprint={selectedBlueprint}
            onSubmit={handleBlueprintSubmit}
            setSelectedBlueprint={setSelectedBlueprint}
            setIsDeploying={setIsDeploying}
          />
        ) : (
          <BluePrintBuilder
            blueprint={selectedBlueprint}
            onSubmit={handleBlueprintSubmit}
            setSelectedBlueprint={setSelectedBlueprint}
            setIsDeploying={setIsDeploying}
          />
        )
      ) : (
        <BlueprintTable
          myPatToken={myPatToken}
          onBlueprintLoad={handleBlueprintLoaded}
          onCreateNew={handleCreateNewBlueprint}
          setIsDeploying={setIsDeploying}
        />
      )}
    </div>
  );
};

const BPManager = () => {
  return <AccessPoint />;
};

export default BPManager;
